// ═══════════════════════════════════════════════════════════════
// FAMETA → Real Columns Migration Tool
// One-time admin page. Reads every Force Account labor and equipment
// record, extracts the FAMETA blob from the notes field, and writes
// formerly-FAMETA fields to their proper GAB columns. Safe to re-run.
//
// Phase 1 (complete): disasterNumber, femaCategory, locationOfWork, sourceFile
// Phase 2 (current):  timeIn, timeOut, totalHours (labor), sourceSheet (labor),
//                     timeIn, timeOut (equipment)
// ═══════════════════════════════════════════════════════════════

import { useState, useCallback } from 'react';
import { Title, Text, Button, Paper, Stack, Group, Badge } from '../../components/gab/GabComponents';
import { IconDatabaseImport, IconCircleCheck, IconAlertTriangle } from '@tabler/icons-react';

const FAMETA_RE = /\|\|\|FAMETA\|\|\|([\s\S]*?)\|\|\|ENDFAMETA\|\|\|/;

function decodeMeta(notes: string): Record<string, string> {
  const m = notes.match(FAMETA_RE);
  if (!m) return {};
  try { return JSON.parse(m[1]); } catch { return {}; }
}

interface MigrationResult {
  laborTotal: number;
  laborMigrated: number;
  laborSkipped: number;
  laborErrors: number;
  equipTotal: number;
  equipMigrated: number;
  equipSkipped: number;
  equipErrors: number;
  log: string[];
}

export default function FametaMigration() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<MigrationResult | null>(null);
  const [dryRun, setDryRun] = useState(true);

  const run = useCallback(async () => {
    setRunning(true);
    setResult(null);

    const log: string[] = [];
    let laborTotal = 0, laborMigrated = 0, laborSkipped = 0, laborErrors = 0;
    let equipTotal = 0, equipMigrated = 0, equipSkipped = 0, equipErrors = 0;

    try {
      const { queryRecords, updateRecord: updateRec } = await import('../../api/client');
      const { TABLES } = await import('../../api/tables');
      const { FIELDS } = await import('../../api/fields');
      const FL = FIELDS.debrisForceAccountLabor;
      const FE = FIELDS.debrisForceAccountEquipment;

      const PAGE = 200;

      // ── Labor ─────────────────────────────────────────────
      {
        let offset = 0, grandTotal = Infinity;
        while (offset < grandTotal) {
          const res = await queryRecords<Record<string, unknown>>(TABLES.debrisForceAccountLabor, {
            limit: PAGE, offset,
          });
          grandTotal = res.total;
          laborTotal = grandTotal;

          for (const row of res.records) {
            const id = Number(row.record_id);
            const rawNotes = String(row[FL.notes] ?? '');
            const meta = decodeMeta(rawNotes);

            const LABOR_FIELDS = [
              { col: FL.disasterNumber, mk: 'disasterNumber' },
              { col: FL.femaCategory,   mk: 'femaCategory' },
              { col: FL.locationOfWork, mk: 'locationOfWork' },
              { col: FL.sourceFile,     mk: 'sourceFile' },
              { col: FL.timeIn,         mk: 'timeIn' },
              { col: FL.timeOut,        mk: 'timeOut' },
              { col: FL.totalHours,     mk: 'totalHours' },
              { col: FL.sourceSheet,    mk: 'sourceSheet' },
            ];

            const needsMigration = LABOR_FIELDS.some(({ col, mk }) => {
              const colVal = row[col];
              const metaVal = meta[mk];
              return metaVal && (colVal === null || colVal === undefined || colVal === '');
            });

            if (!needsMigration) { laborSkipped++; continue; }

            if (dryRun) {
              const fields = LABOR_FIELDS.filter(({ mk }) => meta[mk]).map(({ mk }) => mk).join(', ');
              log.push(`[DRY RUN] Would migrate labor ${id}: ${fields}`);
              laborMigrated++;
              continue;
            }

            try {
              const update: Record<string, string> = {};
              for (const { col, mk } of LABOR_FIELDS) {
                if (meta[mk]) update[col] = meta[mk];
              }
              await updateRec(TABLES.debrisForceAccountLabor, id, update);
              laborMigrated++;
            } catch (e) {
              laborErrors++;
              log.push(`ERROR labor ${id}: ${e instanceof Error ? e.message : String(e)}`);
            }
          }

          offset += PAGE;
          log.push(`Labor progress: ${Math.min(offset, grandTotal).toLocaleString()} / ${grandTotal.toLocaleString()}`);
        }
      }

      // ── Equipment ─────────────────────────────────────────
      {
        let offset = 0, grandTotal = Infinity;
        while (offset < grandTotal) {
          const res = await queryRecords<Record<string, unknown>>(TABLES.debrisForceAccountEquipment, {
            limit: PAGE, offset,
          });
          grandTotal = res.total;
          equipTotal = grandTotal;

          for (const row of res.records) {
            const id = Number(row.record_id);
            const rawNotes = String(row[FE.notes] ?? '');
            const meta = decodeMeta(rawNotes);

            const EQUIP_FIELDS = [
              { col: FE.disasterNumber, mk: 'disasterNumber' },
              { col: FE.femaCategory,   mk: 'femaCategory' },
              { col: FE.locationOfWork, mk: 'locationOfWork' },
              { col: FE.sourceFile,     mk: 'sourceFile' },
              { col: FE.timeIn,         mk: 'timeIn' },
              { col: FE.timeOut,        mk: 'timeOut' },
            ];

            const needsMigration = EQUIP_FIELDS.some(({ col, mk }) => {
              const colVal = row[col];
              const metaVal = meta[mk];
              return metaVal && (colVal === null || colVal === undefined || colVal === '');
            });

            if (!needsMigration) { equipSkipped++; continue; }

            if (dryRun) {
              const fields = EQUIP_FIELDS.filter(({ mk }) => meta[mk]).map(({ mk }) => mk).join(', ');
              log.push(`[DRY RUN] Would migrate equipment ${id}: ${fields}`);
              equipMigrated++;
              continue;
            }

            try {
              const update: Record<string, string> = {};
              for (const { col, mk } of EQUIP_FIELDS) {
                if (meta[mk]) update[col] = meta[mk];
              }
              await updateRec(TABLES.debrisForceAccountEquipment, id, update);
              equipMigrated++;
            } catch (e) {
              equipErrors++;
              log.push(`ERROR equipment ${id}: ${e instanceof Error ? e.message : String(e)}`);
            }
          }

          offset += PAGE;
          log.push(`Equipment progress: ${Math.min(offset, grandTotal).toLocaleString()} / ${grandTotal.toLocaleString()}`);
        }
      }
    } catch (e) {
      log.push(`FATAL: ${e instanceof Error ? e.message : String(e)}`);
    }

    setResult({ laborTotal, laborMigrated, laborSkipped, laborErrors, equipTotal, equipMigrated, equipSkipped, equipErrors, log });
    setRunning(false);
  }, [dryRun]);

  const totalErrors = (result?.laborErrors ?? 0) + (result?.equipErrors ?? 0);

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: '32px 24px' }}>
      <Stack gap={24}>
        <div>
          <Title order={2} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <IconDatabaseImport size={28} color="#4B3FFF" /> FAMETA → Column Migration
          </Title>
          <Text c="dimmed" size="sm">
            Migration for Force Account records. Reads every labor and equipment record,
            extracts the FAMETA JSON blob from the notes field, and writes all formerly-FAMETA
            fields (disasterNumber, femaCategory, locationOfWork, sourceFile, timeIn, timeOut,
            totalHours, sourceSheet) to their proper GAB columns. Safe to re-run.
          </Text>
        </div>

        <Paper p="md" withBorder radius="md" style={{ background: '#fefce8', borderColor: '#fbbf24' }}>
          <Text size="sm" fw={600} style={{ color: '#92400e' }}>Before running in live mode:</Text>
          <Text size="sm" style={{ color: '#92400e', marginTop: 4 }}>
            1. Run dry-run first to confirm the counts look right.<br />
            2. Live mode writes to all labor and equipment records in pages of 200.<br />
            3. Safe to re-run — already-migrated records have empty FAMETA values written over empty columns (no-op).
          </Text>
        </Paper>

        <Paper p="lg" withBorder radius="md">
          <Stack gap={16}>
            <Group gap={12}>
              <Button
                variant={dryRun ? 'filled' : 'light'}
                color={dryRun ? '#4B3FFF' : 'gray'}
                onClick={() => setDryRun(true)}
                size="sm">
                Dry Run (count only)
              </Button>
              <Button
                variant={!dryRun ? 'filled' : 'light'}
                color={!dryRun ? 'var(--color-error)' : 'gray'}
                onClick={() => setDryRun(false)}
                size="sm">
                Live (write to GAB)
              </Button>
            </Group>

            <Button
              size="md"
              loading={running}
              onClick={run}
              color={dryRun ? '#4B3FFF' : 'var(--color-error)'}
              leftSection={<IconDatabaseImport size={18} />}>
              {dryRun ? 'Run Dry Run' : 'Run Migration (LIVE)'}
            </Button>
          </Stack>
        </Paper>

        {result && (
          <Paper p="lg" withBorder radius="md">
            <Stack gap={16}>
              <div>
                <Text size="sm" fw={600} style={{ marginBottom: 8 }}>Labor Records</Text>
                <Group gap={10}>
                  <Badge size="lg" color="#4B3FFF">Total: {result.laborTotal.toLocaleString()}</Badge>
                  <Badge size="lg" color="var(--color-success)">Migrated: {result.laborMigrated.toLocaleString()}</Badge>
                  <Badge size="lg" color="gray">Skipped: {result.laborSkipped.toLocaleString()}</Badge>
                  {result.laborErrors > 0 && <Badge size="lg" color="var(--color-error)">Errors: {result.laborErrors}</Badge>}
                </Group>
              </div>
              <div>
                <Text size="sm" fw={600} style={{ marginBottom: 8 }}>Equipment Records</Text>
                <Group gap={10}>
                  <Badge size="lg" color="#4B3FFF">Total: {result.equipTotal.toLocaleString()}</Badge>
                  <Badge size="lg" color="var(--color-success)">Migrated: {result.equipMigrated.toLocaleString()}</Badge>
                  <Badge size="lg" color="gray">Skipped: {result.equipSkipped.toLocaleString()}</Badge>
                  {result.equipErrors > 0 && <Badge size="lg" color="var(--color-error)">Errors: {result.equipErrors}</Badge>}
                </Group>
              </div>

              {totalErrors === 0 && !dryRun && (
                <Group gap={8} style={{ color: 'var(--color-success)' }}>
                  <IconCircleCheck size={20} />
                  <Text size="sm" fw={600}>Migration complete. FAMETA data is now in real columns.</Text>
                </Group>
              )}

              {totalErrors > 0 && (
                <Group gap={8} style={{ color: 'var(--color-error)' }}>
                  <IconAlertTriangle size={20} />
                  <Text size="sm" fw={600}>{totalErrors} records failed — check the log below.</Text>
                </Group>
              )}

              {result.log.length > 0 && (
                <div style={{
                  background: '#0f172a', color: '#94a3b8', borderRadius: 8,
                  padding: '12px 16px', fontFamily: 'monospace', fontSize: '0.78rem',
                  maxHeight: 300, overflowY: 'auto',
                }}>
                  {result.log.map((line, i) => <div key={i}>{line}</div>)}
                </div>
              )}
            </Stack>
          </Paper>
        )}
      </Stack>
    </div>
  );
}
